/**
 * The units a supplement's dosage is counted in.
 *
 * Backed by TanStack Query rather than fetched fresh with every opening of the
 * create screen: the list barely changes, so a workspace that has loaded it
 * once this session gets it back instantly, and only a quiet background read
 * checks whether it has moved. The same arrangement `useExerciseTypes` makes,
 * for the same reason.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchSupplementUnits } from "../services/supplementUnitsService";
import type { SupplementUnitOption } from "../types/supplements.types";

export type SupplementUnitsStatus = "loading" | "ready" | "failed";

export interface SupplementUnitsState {
  options: SupplementUnitOption[];
  status: SupplementUnitsStatus;
}

const NO_UNITS: SupplementUnitOption[] = [];

export const SUPPLEMENT_UNITS_QUERY_KEY = ["supplements", "units"] as const;

export function useSupplementUnits(): SupplementUnitsState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: SUPPLEMENT_UNITS_QUERY_KEY,
    queryFn: ({ signal }) => fetchSupplementUnits(getToken, signal),
  });

  useEffect(() => {
    if (query.isError)
      console.error("Supplement units failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    options: query.data ?? NO_UNITS,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}
